import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrandMark, DesktopHeaderBrand, HeaderActionsMenu, LoadingBrandPulse, SettingsGearIcon, StatusBadge, TripMetric, UserAvatar, ViewerPositionIcon, type HeaderMenuAction } from "../../components";
import type { SharedTrip, SharedTripViewProps } from "./types";
import { publicInformationMenuLinks } from "../../app/publicRoutes";
import "./shared-trip.css";

function MountainIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false"><path d="m3 18 6-10 3 5 2-3 7 8H3Z" /></svg>
  );
}

function DeviceIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false"><rect x="6" y="2.5" width="12" height="19" rx="2" /><path d="M10 5h4M11 18.5h2" /></svg>
  );
}

function ViewsIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <path d="M12 3v11m-4-4 4 4 4-4M5 16v4h14v-4" />
    </svg>
  );
}

function StartIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" />
    </svg>
  );
}

function EndIcon() {
  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <path d="M6 21V4m0 1h11l-2.3 3L17 11H6" />
    </svg>
  );
}

function DirectionsIcon() {
  return (
    <svg className="header-menu__directions-icon" viewBox="0 0 256 256" focusable="false" aria-hidden="true">
      <path d="M128 0 149 33v51c0 13 7 20 20 20h15V78l72 45-72 45v-25h-35v91c0 14-9 22-21 22s-22-8-22-22v-68H78v25L0 146l78-45v25h28V33L128 0Z" />
    </svg>
  );
}

const ELEVATION_EXPANDED_KEY = "mapapal.elevation-expanded";

function readElevationExpanded(): boolean {
  try {
    return window.localStorage.getItem(ELEVATION_EXPANDED_KEY) === "true";
  } catch {
    return false;
  }
}

function ElevationDisclosure({ trip, elevationSlot, current, forceExpanded = false }: { trip: SharedTrip; elevationSlot?: ReactNode; current?: string; forceExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(readElevationExpanded);
  if (!elevationSlot || !trip.elevation) return null;
  const isExpanded = forceExpanded || expanded;

  const toggle = () => {
    setExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(ELEVATION_EXPANDED_KEY, String(next));
      } catch {
        // The preference remains valid for this page if storage is unavailable.
      }
      return next;
    });
  };

  return (
    <section className={`shared-trip__elevation-disclosure${isExpanded ? " shared-trip__elevation-disclosure--expanded" : ""}${forceExpanded ? " shared-trip__elevation-disclosure--fixed" : ""}`} aria-label="Elevation profile">
      {forceExpanded ? (
        <div className="shared-trip__elevation-heading">
          <span><MountainIcon /> Elevation {current ? <output className="shared-trip__elevation-current" data-testid="elevation-current" htmlFor="trip-progress">{current}</output> : null}</span>
        </div>
      ) : (
        <button type="button" aria-controls="trip-elevation-content" aria-expanded={expanded} onClick={toggle}>
          <span><MountainIcon /> Elevation {current ? <output className="shared-trip__elevation-current" data-testid="elevation-current" htmlFor="trip-progress">{current}</output> : null}</span>
          <span className="shared-trip__elevation-action">{expanded ? "Hide graph" : "Show graph"}<span aria-hidden="true">⌄</span></span>
        </button>
      )}
      {isExpanded ? (
        <div id="trip-elevation-content" className="shared-trip__elevation-content">
          {elevationSlot}
        </div>
      ) : null}
    </section>
  );
}

function EmptyMap() {
  return (
    <div className="shared-trip__map-placeholder" role="img" aria-label="Map loading">
      <svg viewBox="0 0 320 176" preserveAspectRatio="none" aria-hidden="true">
        <path d="M-10 143c48-28 76-17 108-55s70-35 101-9 66 14 131-34" />
        <path d="M-8 50c43 12 75 9 105-20m125 156c-11-34 3-52 27-76 18-18 26-40 25-71" />
      </svg>
      <span className="shared-trip__map-pin" />
    </div>
  );
}

function StateScreen({
  kind,
  message,
  onRetry,
  onBack,
  embedded = false,
}: {
  kind: "not-found" | "access-denied" | "error";
  message?: string;
  onRetry?: () => void;
  onBack?: () => void;
  embedded?: boolean;
}) {
  const content = {
    "not-found": {
      eyebrow: "Trip not found",
      title: "This link no longer seems to work",
      body: "Check that you received the complete link, or ask the sender to share the trip again.",
    },
    "access-denied": {
      eyebrow: "Private trip",
      title: "You do not have access to this trip",
      body: "Sign in with the account this trip was shared with, or ask the sender to update its sharing settings.",
    },
    error: {
      eyebrow: "Something went wrong",
      title: "The trip could not be loaded",
      body: message ?? "Check your connection and try again in a moment.",
    },
  }[kind];

  return (
    <main className={`shared-trip shared-trip--centered${embedded ? " shared-trip--embedded" : ""}`}>
      <section className="trip-state" aria-labelledby="trip-state-title">
        <div className={`trip-state__symbol trip-state__symbol--${kind}`} aria-hidden="true">
          {kind === "access-denied" ? "●" : kind === "not-found" ? "?" : "!"}
        </div>
        <p className="trip-state__eyebrow">{content.eyebrow}</p>
        <h1 id="trip-state-title">{content.title}</h1>
        <p>{content.body}</p>
        <div className="trip-state__actions">
          {kind === "error" && onRetry ? (
            <button className="trip-button trip-button--primary" type="button" onClick={onRetry}>Try again</button>
          ) : null}
          {onBack ? (
            <button className="trip-button trip-button--secondary" type="button" onClick={onBack}>Go to home</button>
          ) : null}
        </div>
      </section>
    </main>
  );
}

function ExpandableTripHeading({ title, untitled }: { title: string; untitled: boolean }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  useEffect(() => {
    const titleElement = titleRef.current;
    if (!titleElement || expanded) return;

    const updateOverflow = () => setCanExpand(titleElement.scrollWidth > titleElement.clientWidth + 1);
    updateOverflow();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(titleElement);
    return () => observer.disconnect();
  }, [expanded, title]);

  return (
    <div className="trip-summary__title-row">
      <h1
        ref={titleRef}
        id="trip-title"
        className={`${untitled ? "trip-summary__title--untitled" : ""}${expanded ? " trip-summary__title--expanded" : ""}`.trim() || undefined}
      >
        {title}
      </h1>
      {canExpand ? (
        <button className="trip-summary__title-toggle" type="button" aria-expanded={expanded} onClick={() => setExpanded((current) => !current)}>
          {expanded ? "Less" : "More"}
        </button>
      ) : null}
    </div>
  );
}

function TripIdentity({ trip, actions }: { trip: SharedTrip; actions?: ReactNode }) {
  return (
    <section className="trip-summary trip-summary--timeline" aria-labelledby="trip-title" data-testid="trip-route-summary">
      <div className="trip-summary__topline">
        <div className="trip-summary__identity">
          <UserAvatar username={trip.ownerName} className="trip-summary__avatar" />
          <div className="trip-summary__identity-copy">
            <p className="trip-summary__owner">{trip.ownerName}</p>
            <ExpandableTripHeading title={trip.title} untitled={Boolean(trip.isUntitled)} />
          </div>
        </div>
        <div className="trip-summary__topline-controls">
          {actions ? <div className="trip-summary__topline-action">{actions}</div> : null}
          <div className="trip-summary__topline-side">
            <StatusBadge tone={trip.status === "ended" ? "success" : trip.status === "paused" ? "warning" : "danger"}>{trip.statusLabel}</StatusBadge>
            {trip.description ? <span className="trip-summary__description">{trip.description}</span> : null}
          </div>
        </div>
      </div>
      <section className="trip-summary__key-metrics" aria-label="Key trip statistics">
        <div>
          <small>Started</small>
          <time className="trip-summary__started" dateTime={trip.startedAt} title={trip.startedTitle}>
            <strong>{trip.startedLabel.replace(/^Started\s*/i, "")}</strong>
          </time>
        </div>
        <div><small>Duration</small><strong>{trip.duration}</strong></div>
        <div><small>Distance</small><strong>{trip.distance}</strong></div>
        <div><small>Average speed</small><strong>{trip.averageSpeed}</strong></div>
      </section>
    </section>
  );
}

function TripDownloadAction({ onDownload }: { onDownload: () => Promise<"saved" | "cancelled"> }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const download = async () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      await onDownload();
    } catch {
      setError("The CSV file could not be downloaded. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="trip-download">
      <button type="button" aria-label="Download trip as CSV" disabled={loading} onClick={() => void download()}>
        <span className="trip-download__icon"><DownloadIcon /></span>
        <span><strong>{loading ? "Preparing CSV…" : "Download CSV"}</strong><small>Save recorded positions to import into GIS and mapping services.</small></span>
      </button>
      {error ? <p role="alert">{error}</p> : null}
    </div>
  );
}

function TripJourneyDetails({ trip, onDownloadCsv }: { trip: SharedTrip; onDownloadCsv?: () => Promise<"saved" | "cancelled"> }) {
  const secondaryMetricCount = 2 + Number(Boolean(trip.platform)) + Number(trip.views !== undefined) + Number(Boolean(onDownloadCsv))
  return (
    <div className="shared-trip__content">
      <section className={`trip-metrics trip-metrics--secondary trip-metrics--columns-${secondaryMetricCount}`} aria-label="Trip details" data-testid="trip-dates">
        <TripMetric icon={<StartIcon />} label="Start" value={trip.startedAt} />
        <TripMetric icon={<EndIcon />} label="End" value={trip.endedAt ?? "In progress"} />
        {trip.platform ? <TripMetric icon={<DeviceIcon />} label="Platform" value={trip.platform} /> : null}
        {trip.views !== undefined ? <TripMetric icon={<ViewsIcon />} label="Views" value={new Intl.NumberFormat('en-GB').format(trip.views)} /> : null}
        {onDownloadCsv ? <TripDownloadAction onDownload={onDownloadCsv} /> : null}
      </section>
    </div>
  );
}

export function SharedTripView({ state, embedded = false, desktopDetailsLayout = false, comparisonMode = false, mapSlot, elevationSlot, elevationCurrent, tripActions, onDownloadCsv, onRetry, onBack, onHome, onOpenDirections, onToggleViewerPosition, viewerPositionActive = false, onOpenSettings, signedInUsername, onOpenProfile }: SharedTripViewProps) {
  if (state.kind === "loading") {
    return (
      <main className={`shared-trip shared-trip--loading${embedded ? " shared-trip--embedded" : ""}`} aria-busy="true" aria-label="Loading trip">
        <div className="trip-loading__brand" aria-hidden="true">
          <LoadingBrandPulse className="trip-loading__brand-icon" />
          <span>Loading..</span>
        </div>
      </main>
    );
  }

  if (state.kind !== "ready") {
    return <StateScreen kind={state.kind} message={state.kind === "error" ? state.message : undefined} onRetry={onRetry} onBack={onBack} embedded={embedded} />;
  }

  const headerActions: HeaderMenuAction[] = [];
  if (onOpenDirections) headerActions.push({ label: "Show directions to target", icon: <DirectionsIcon />, onSelect: onOpenDirections });
  if (onToggleViewerPosition) headerActions.push({ label: "Toggle your position", icon: <ViewerPositionIcon />, checked: viewerPositionActive, onSelect: onToggleViewerPosition });
  if (onOpenSettings) headerActions.push({ label: "Settings", icon: <SettingsGearIcon />, onSelect: onOpenSettings });
  headerActions.push(...publicInformationMenuLinks());
  const useDesktopDetailsLayout = embedded || desktopDetailsLayout;
  const elevationPanel = <ElevationDisclosure trip={state.trip} elevationSlot={elevationSlot} current={elevationCurrent} forceExpanded={useDesktopDetailsLayout} />;

  return (
    <main className={`shared-trip${embedded ? " shared-trip--embedded" : ""}${useDesktopDetailsLayout ? " shared-trip--desktop-details" : ""}${comparisonMode ? " shared-trip--comparison" : ""}`}>
      {!embedded ? <header className="shared-trip__header">
        <div className="shared-trip__header-inner">
          <div className="shared-trip__header-side shared-trip__header-side--left">
            <BrandMark iconOnly href={import.meta.env.BASE_URL} onNavigate={onHome} />
          </div>
          <DesktopHeaderBrand />
          <div className="shared-trip__header-side shared-trip__header-side--right">
            {signedInUsername && onOpenProfile ? (
              <button className="shared-trip__profile" type="button" aria-label="Open profile settings" title="Open profile settings" onClick={onOpenProfile}>
                <UserAvatar username={signedInUsername} className="shared-trip__profile-avatar" />
                <span><strong>{signedInUsername}</strong><small>Signed in</small></span>
              </button>
            ) : null}
            {headerActions.length ? <HeaderActionsMenu actions={headerActions} /> : null}
          </div>
        </div>
      </header> : null}
      <div className="shared-trip__viewer">
        <section className="shared-trip__map" aria-label={comparisonMode ? "Trip comparison map" : "Trip map"} data-testid="trip-map">
          {mapSlot ? mapSlot(<TripIdentity trip={state.trip} actions={tripActions} />, useDesktopDetailsLayout ? elevationPanel : undefined) : <EmptyMap />}
        </section>
        {!mapSlot ? <div className="shared-trip__priority-content"><TripIdentity trip={state.trip} actions={tripActions} /></div> : null}
        {!useDesktopDetailsLayout ? elevationPanel : null}
        {!comparisonMode ? <TripJourneyDetails trip={state.trip} onDownloadCsv={onDownloadCsv} /> : null}
      </div>
    </main>
  );
}
